import { Button } from '@crowdex/ui/components/button';
import { ColorField } from '@crowdex/ui/components/color-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Input } from '@crowdex/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Slider } from '@crowdex/ui/components/slider';
import { Textarea } from '@crowdex/ui/components/textarea';
import { toast } from '@crowdex/ui/components/toaster';
import {
  type AppearanceVariant,
  type ChromeTheme,
  CROWDEX_CHROME,
  normalizeFontName,
} from '@crowdex/ui/lib/appearance/chrome-theme';
import { chromeFromVscodeTheme } from '@crowdex/ui/lib/appearance/vscode-theme';
import { Ellipsis } from 'lucide-react';
import { useRef, useState } from 'react';
import { SettingRow, SettingsGroup } from '@/components/settings-page/setting-controls';
import {
  type Appearance,
  CROWDEX_CODE_THEME,
  exportAppearance,
  importAppearance,
  type VariantAppearance,
} from '@/lib/appearance/appearance';
import { usePluginCodeThemes } from '@/lib/appearance/appearance-provider';
import {
  type CodeThemeOption,
  codeThemeOptions,
  importedTheme,
  importVscodeTheme,
  removeImportedTheme,
  resolveCodeTheme,
  useImportedThemesRevision,
} from '@/lib/appearance/code-themes';
import { errorMessage } from '@/lib/error-message';

const VARIANT_LABEL: Record<AppearanceVariant, string> = { light: 'Light', dark: 'Dark' };

const SOURCE_LABEL: Record<CodeThemeOption['source'], string> = {
  crowdex: 'Crowdex',
  plugin: 'From plugins',
  imported: 'Imported',
  bundled: 'Themes',
};

function groupBySource(options: readonly CodeThemeOption[]) {
  const groups = new Map<CodeThemeOption['source'], CodeThemeOption[]>();
  for (const option of options)
    groups.set(option.source, [...(groups.get(option.source) ?? []), option]);
  return [...groups.entries()];
}

function VariantThemeSettings({
  variant,
  value,
  onChange,
  onImportShared,
}: {
  variant: AppearanceVariant;
  value: VariantAppearance;
  onChange: (value: VariantAppearance) => void;
  onImportShared: () => void;
}) {
  const label = VARIANT_LABEL[variant];
  const pluginThemes = usePluginCodeThemes(true);
  const importedRevision = useImportedThemesRevision();
  const fileInput = useRef<HTMLInputElement>(null);
  // `importedRevision` re-reads the imported themes after an import or a removal.
  const options = importedRevision >= 0 ? codeThemeOptions(variant, pluginThemes) : [];
  // The latest value and selection, for answers that arrive after the user moved on.
  const latest = useRef(value);
  latest.current = value;
  const selection = useRef(0);
  const selected = options.some((option) => option.id === value.codeTheme)
    ? value.codeTheme
    : CROWDEX_CODE_THEME;
  const chrome = value.chrome;
  const setChrome = (patch: Partial<ChromeTheme>) =>
    onChange({ ...value, chrome: { ...chrome, ...patch } });

  async function selectCodeTheme(id: string) {
    const request = ++selection.current;
    try {
      const next =
        id === CROWDEX_CODE_THEME
          ? CROWDEX_CHROME[variant]
          : chromeFromVscodeTheme(await resolveCodeTheme(id, variant, pluginThemes));
      // A slower theme picked earlier must not replace the one picked last.
      if (request !== selection.current) return;
      // The fonts are the user's, not the theme's.
      onChange({ codeTheme: id, chrome: { ...next, fonts: latest.current.chrome.fonts } });
    } catch (error) {
      if (request === selection.current) toast.error('Could not apply theme', errorMessage(error));
    }
  }

  async function importFile(file: File) {
    try {
      const option = await importVscodeTheme(await file.text(), file.name);
      if (option.type !== variant) {
        toast.info(
          `${option.label} imported`,
          `It is a ${option.type} theme: pick it in the ${VARIANT_LABEL[option.type].toLowerCase()} theme.`,
        );
        return;
      }
      await selectCodeTheme(option.id);
      toast.success(`${option.label} imported`);
    } catch (error) {
      toast.error('Couldn’t import the theme', errorMessage(error));
    }
  }

  return (
    <SettingsGroup title={`${label} theme`}>
      <SettingRow
        description="Colors the code and terminal, and gives the app its background, text and accent."
        title="Theme"
      >
        <div className="flex items-center gap-1.5">
          <Select onValueChange={(id) => void selectCodeTheme(id)} value={selected}>
            <SelectTrigger aria-label={`${label} theme`} className="w-full sm:w-48" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {groupBySource(options).map(([source, group]) => (
                <SelectGroup key={source}>
                  {source !== 'crowdex' && <SelectLabel>{SOURCE_LABEL[source]}</SelectLabel>}
                  {group.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-label={`${label} theme actions`} size="icon-sm" variant="ghost">
                <Ellipsis className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => fileInput.current?.click()}>
                Import VS Code theme…
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  const shared = exportAppearance(
                    value,
                    variant,
                    selected.startsWith('imported:') ? importedTheme(selected) : null,
                  );
                  void navigator.clipboard.writeText(shared).then(
                    () => toast.success(`${label} theme copied`),
                    () => toast.error(`Couldn’t copy the ${label.toLowerCase()} theme`),
                  );
                }}
              >
                Copy theme
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onImportShared}>Paste theme…</DropdownMenuItem>
              {selected.startsWith('imported:') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      void removeImportedTheme(selected).then(() =>
                        selectCodeTheme(CROWDEX_CODE_THEME),
                      );
                    }}
                    variant="destructive"
                  >
                    Remove imported theme
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            accept=".json,.jsonc,application/json"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importFile(file);
            }}
            ref={fileInput}
            type="file"
          />
        </div>
      </SettingRow>
      <SettingRow description="Buttons, links, focus and the send button." title="Accent">
        <ColorField
          aria-label={`${label} accent color`}
          onValueChange={(accent) => setChrome({ accent })}
          value={chrome.accent}
        />
      </SettingRow>
      <SettingRow description="The surface every panel is derived from." title="Background">
        <ColorField
          aria-label={`${label} background color`}
          onValueChange={(surface) => setChrome({ surface })}
          value={chrome.surface}
        />
      </SettingRow>
      <SettingRow description="Text and icons." title="Foreground">
        <ColorField
          aria-label={`${label} foreground color`}
          onValueChange={(ink) => setChrome({ ink })}
          value={chrome.ink}
        />
      </SettingRow>
      <SettingRow
        description="How distinct panels, lines and secondary text are from the background."
        title="Contrast"
      >
        <div className="flex w-full items-center gap-3 sm:w-48">
          <Slider
            aria-label={`${label} contrast`}
            max={100}
            min={0}
            onValueChange={([contrast]) => {
              if (contrast !== undefined) setChrome({ contrast });
            }}
            step={1}
            value={[chrome.contrast]}
          />
          <span className="w-7 shrink-0 text-right text-muted-foreground text-xs tabular-nums">
            {chrome.contrast}
          </span>
        </div>
      </SettingRow>
      <FontRow
        description="The interface font. Leave empty for the system font."
        label={`${label} UI font`}
        onChange={(ui) => setChrome({ fonts: { ...chrome.fonts, ui } })}
        title="UI font"
        value={chrome.fonts.ui}
      />
      <FontRow
        description="Code blocks, diffs and the terminal. Leave empty for the system font."
        label={`${label} code font`}
        onChange={(code) => setChrome({ fonts: { ...chrome.fonts, code } })}
        title="Code font"
        value={chrome.fonts.code}
      />
    </SettingsGroup>
  );
}

function FontRow({
  description,
  label,
  onChange,
  title,
  value,
}: {
  description: string;
  label: string;
  onChange: (value: string | null) => void;
  title: string;
  value: string | null;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <SettingRow description={description} title={title}>
      <Input
        aria-label={label}
        className="w-full sm:w-48"
        onBlur={() => {
          if (draft === null) return;
          onChange(normalizeFontName(draft));
          setDraft(null);
        }}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        placeholder="System"
        spellCheck={false}
        value={draft ?? value ?? ''}
      />
    </SettingRow>
  );
}

function PasteThemeDialog({
  open,
  onOpenChange,
  onImport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (variant: AppearanceVariant, value: VariantAppearance) => void;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setText('');
          setError(null);
        }
      }}
      open={open}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Paste theme</DialogTitle>
          <DialogDescription>
            A theme copied from Crowdex, starting with <code>crowdex-theme-v1:</code>.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="Theme share string"
          className="min-h-28 font-mono text-xs"
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
          }}
          spellCheck={false}
          value={text}
        />
        {error && <p className="text-destructive text-xs">{error}</p>}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={text.trim().length === 0}
            onClick={() => {
              void (async () => {
                try {
                  const shared = importAppearance(text);
                  let value = shared.value;
                  // A theme imported on the other device is kept here too, under its own id.
                  if (shared.importedTheme !== null) {
                    const option = await importVscodeTheme(
                      JSON.stringify(shared.importedTheme),
                      'Imported theme',
                    );
                    value = { ...value, codeTheme: option.id };
                  }
                  onImport(shared.variant, value);
                  onOpenChange(false);
                  setText('');
                  toast.success(`${VARIANT_LABEL[shared.variant]} theme imported`);
                } catch (cause) {
                  setError(errorMessage(cause));
                }
              })();
            }}
          >
            Import theme
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The light and dark themes: a code theme each, and the chrome it gives the app. */
export function AppearanceThemeSettings({
  appearance,
  onChange,
}: {
  appearance: Appearance;
  onChange: (appearance: Appearance) => void;
}) {
  const [pasting, setPasting] = useState(false);
  // Changes can resolve after another one: each applies to the appearance as it is by then.
  const latest = useRef(appearance);
  latest.current = appearance;
  const change = (variant: AppearanceVariant, value: VariantAppearance) => {
    latest.current = { ...latest.current, [variant]: value };
    onChange(latest.current);
  };
  return (
    <>
      {(['light', 'dark'] as const).map((variant) => (
        <VariantThemeSettings
          key={variant}
          onChange={(value) => change(variant, value)}
          onImportShared={() => setPasting(true)}
          value={appearance[variant]}
          variant={variant}
        />
      ))}
      <PasteThemeDialog onImport={change} onOpenChange={setPasting} open={pasting} />
    </>
  );
}
